import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block mb-6">
                        <button className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 border border-gray-300 flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Home
                        </button>
                    </Link>
                    <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-mono mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8 space-y-8">
                    {/* Introduction */}
                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-blue-500 pl-4">1. Introduction</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Welcome to The Catchphraser! We are committed to protecting your privacy 
                            and ensuring the security of your personal information. This Privacy Policy explains how we collect, 
                            use, and safeguard your data when you use our pronunciation training platform.
                        </p>
                    </section>

                    {/* Audio Processing & AI Assessment */}
                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-green-500 pl-4">2. Audio Processing & Analysis</h2>
                        
                        <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Comprehensive Audio Analysis
                            </h3>
                            <div className="space-y-3 text-gray-700">
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <p><strong>Microsoft Pronunciation Assessment API:</strong> For detailed phoneme-level pronunciation analysis, we use Microsoft's AI technology</p>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <p><strong>Internal Intonation Analysis:</strong> We process pitch contours, intensity curves, and rhythm patterns internally on our servers</p>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <p><strong>Statistical Processing:</strong> All advanced intonation and prosody analysis happens internally using our proprietary algorithms</p>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <p><strong>Fully Anonymous External Processing:</strong> Audio sent to Microsoft contains no user identification, metadata, or source information</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6 mt-4">
                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    Guest User Processing
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Guest audio is processed in real-time only - no recordings or personal data are stored or collected for guest sessions.
                                </p>
                            </div>
                            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <svg className="w-4 h-4 text-purple-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                    </svg>
                                    Registered User Benefits
                                </h4>
                                <p className="text-sm text-gray-600">
                                    Registered users get persistent storage for progress tracking, statistics, and personalized learning analytics.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Data Collection & Storage */}
                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-purple-500 pl-4">3. Data Collection & Storage</h2>
                        
                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Guest Users</h4>
                                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                                    <li><strong>No data collection:</strong> Audio is processed in real-time and immediately discarded</li>
                                    <li><strong>No persistent storage:</strong> No recordings, progress, or personal information is stored</li>
                                    <li><strong>Session-only processing:</strong> All analysis happens during your current browser session</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Registered Users</h4>
                                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                                    <li>Username and email address for account creation</li>
                                    <li>Pronunciation assessment scores and progress metrics</li>
                                    <li>Practice history and learning statistics</li>
                                    <li>Audio recordings (optional, user-controlled storage)</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Technical Information</h4>
                                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                                    <li>Browser type and version (anonymized)</li>
                                    <li>Session cookies for functionality</li>
                                    <li>Device information for optimization</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Processing Technologies */}
                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-amber-500 pl-4">4. Analysis Technologies</h2>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                    <svg className="w-5 h-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    External AI Processing
                                </h4>
                                <div className="space-y-2 text-sm text-gray-700">
                                    <p><strong>Microsoft Pronunciation Assessment:</strong> Used for precise phoneme-level accuracy scoring</p>
                                    <p><strong>Anonymous Data Only:</strong> No user identification sent with API requests</p>
                                    <p><strong>Secure Transmission:</strong> All external API calls use encrypted connections</p>
                                </div>
                            </div>
                            
                            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                    </svg>
                                    Internal Processing
                                </h4>
                                <div className="space-y-2 text-sm text-gray-700">
                                    <p><strong>Intonation Analysis:</strong> Pitch contour and rhythm pattern processing</p>
                                    <p><strong>Intensity Analysis:</strong> Volume and stress pattern detection</p>
                                    <p><strong>Statistical Analytics:</strong> Progress tracking and learning pattern analysis</p>
                                    <p><strong>Visual Feedback:</strong> Real-time audio waveform and pitch visualization</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Data Rights & Control */}
                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-red-500 pl-4">5. Your Data Rights</h2>
                        
                        <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Complete Control Over Your Data
                            </h3>
                            
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-lg p-4 border">
                                    <h4 className="font-semibold text-gray-800 mb-2">Access & Download</h4>
                                    <p className="text-sm text-gray-600">
                                        Download your learning data, progress statistics, and audio recordings at any time.
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border">
                                    <h4 className="font-semibold text-gray-800 mb-2">Delete Recordings</h4>
                                    <p className="text-sm text-gray-600">
                                        Remove individual recordings or clear your entire practice history instantly.
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border">
                                    <h4 className="font-semibold text-gray-800 mb-2">Account Deletion</h4>
                                    <p className="text-sm text-gray-600">
                                        Permanently delete your account and all associated data upon request.
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border">
                                    <h4 className="font-semibold text-gray-800 mb-2">Data Export</h4>
                                    <p className="text-sm text-gray-600">
                                        Request a complete export of all your data in standardized formats.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Contact Information */}
                    <section className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-indigo-500 pl-4">6. Contact Us</h2>
                        
                        <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                            <div className="flex items-start">
                                <svg className="w-6 h-6 text-indigo-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <div>
                                    <p className="text-indigo-700 font-semibold">Privacy Questions & Data Requests</p>
                                    <p className="text-indigo-600 mt-1">
                                        Contact us at <span className="font-semibold">info@catchphraser.com</span> for privacy-related questions, 
                                        data deletion requests, or technical information about our processing methods.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Footer */}
                    <div className="border-t border-gray-200 pt-6 text-center">
                        <p className="text-sm text-gray-500">
                            This privacy policy may be updated periodically. We will notify you of any significant changes.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;